import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { CaseObject } from 'dashboard/models/case';
import { DocumentGeneric } from 'dashboard/models/document';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Report } from 'dashboard/models/report';
import { ReportRun } from 'dashboard/models/report-run';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Verification } from 'dashboard/models/verification';
import { LAYOUT_TABLE_SUPPORTED } from 'dashboard/modules/Resource/Layouts/constants';
import LayoutList from 'dashboard/modules/Resource/Layouts/LayoutList';
import LayoutTable from 'dashboard/modules/Resource/Layouts/LayoutTable';
import { ModuleResource } from 'dashboard/modules/Resource/model';
import ResourcePlaceholder from 'dashboard/modules/Resource/ResourcePlaceholder';
import { ModuleResourceAccount } from 'dashboard/modules/ResourceAccount';
import ResourceAccountV2 from 'dashboard/modules/ResourceAccount/ResourceAccountV2';
import ResourceInquiry, { ModuleResourceInquiry } from 'dashboard/modules/ResourceInquiry';
import ResourceReport, { ModuleResourceReport } from 'dashboard/modules/ResourceReport';
import ResourceReportRun, { ModuleResourceReportRun } from 'dashboard/modules/ResourceReportRun';
import ResourceTransaction, {
  generateColumnDefs as generateColumnDefsTxns,
  generateColumnIdToLabel as generateColumnIdToLabelTxns,
  generateTableData as generateTableDataTxns,
  ModuleResourceTransaction,
} from 'dashboard/modules/ResourceTransaction';
import { ResourceTransactionAttribute } from 'dashboard/modules/ResourceTransaction/model';
import ResourceVerification, {
  ModuleResourceVerification,
} from 'dashboard/modules/ResourceVerification';
import { ResourceVerificationModules } from 'dashboard/modules/ResourceVerification/model';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export interface ResourceProps {
  module: ModuleResource;
  fields: Record<string, CaseObject[]>;
  documents: DocumentGeneric[];
  tags: Tag[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  inquiryVerifications: Verification[];
  allInquiryTemplates: (InquiryTemplate | Template)[];
  transactionTypes: TransactionType[];
  transactionStatuses: TransactionStatus[];
  accountTypes: AccountType[];
  accountStatuses: AccountStatus[];
  reportTemplates: ReportTemplate[];
  featureFlags: FeatureFlagsMap;
  isLoading: boolean;
  handleUpdateInquiryTags: (inquiryId: string, tags: string[]) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleUpdateReportTags: (reportId: string, tags: string[]) => void;
}

const Resource = ({
  module,
  fields,
  documents,
  tags,
  inquiryTemplates,
  inquiryTemplateVersions,
  inquiryVerifications,
  allInquiryTemplates,
  transactionTypes,
  transactionStatuses,
  accountTypes,
  accountStatuses,
  reportTemplates,
  featureFlags,
  isLoading,
  handleUpdateInquiryTags,
  handleFlagInquiryIssues,
  handleFlagVerificationIssues,
  handleUpdateReportTags,
}: ResourceProps) => {
  const resources = resolveFieldKey(fields, module) || [];
  const viewLayout = module['view-layout'] ?? 'list';
  if (isLoading || resources.length === 0) {
    return (
      <ResourcePlaceholder
        module={module}
        allInquiryTemplates={allInquiryTemplates}
        isLoading={isLoading}
      />
    );
  }

  switch (viewLayout) {
    case 'list': {
      const displayItem = (data: CaseObject, index: number) => {
        return (
          <div key={`resource-view__${module.token}--${index}`}>
            <ResourceView
              module={module}
              resource={data}
              documents={documents}
              inquiryTemplates={inquiryTemplates}
              inquiryTemplateVersions={inquiryTemplateVersions}
              inquiryVerifications={inquiryVerifications}
              transactionTypes={transactionTypes}
              transactionStatuses={transactionStatuses}
              accountTypes={accountTypes}
              accountStatuses={accountStatuses}
              reportTemplates={reportTemplates}
              featureFlags={featureFlags}
              tags={tags}
              handleUpdateInquiryTags={handleUpdateInquiryTags}
              handleFlagInquiryIssues={handleFlagInquiryIssues}
              handleFlagVerificationIssues={handleFlagVerificationIssues}
              handleUpdateReportTags={handleUpdateReportTags}
            />
          </div>
        );
      };
      return <LayoutList resources={resources} displayItem={displayItem} />;
    }
    case 'table': {
      if (LAYOUT_TABLE_SUPPORTED.includes(module['field-key'])) {
        const {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          cardTitle,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nullStateText,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          defaultVisibleKeys,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          columnIdToLabel,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          columnDefs,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tableData,
        } = generateTableParams({
          resources: resources,
          transactionTypes: transactionTypes,
          tags: tags,
          transactionStatuses: transactionStatuses,
          // NOTE(brandon): A bit of a hack to check array length since txn modules have unused empty attributes right now and we don't want all txn modules to suddenly display nothing by default.
          defaultVisibleKeys:
            module?.attributes.length > 0
              ? module.attributes
              : Object.values(ResourceTransactionAttribute),
        });
        return (
          <LayoutTable
            cardTitle={cardTitle}
            nullStateText={nullStateText}
            defaultVisibleKeys={defaultVisibleKeys}
            columnIdToLabel={columnIdToLabel}
            columnDefs={columnDefs}
            tableData={tableData}
          />
        );
      }
    }
  }

  return null;
};

const resolveFieldKey = (fields: Record<string, CaseObject[]>, module: ModuleResource) => {
  const { 'field-key': fieldKey } = module;

  switch (fieldKey) {
    case 'reports': {
      const reportTemplateType = (module as ModuleResourceReport)['report-template-type'];
      return fields[fieldKey].filter(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (rep) => (rep as Report).relationships['report-template'].data.type === reportTemplateType,
      );
    }
    case 'report-runs': {
      const reportTemplateType = (module as ModuleResourceReportRun)['report-template-type'];
      return fields[fieldKey].filter(
        (rep) =>
          (rep as ReportRun).relationships['report-template'].data.type === reportTemplateType,
      );
    }
    case 'verifications': {
      const verificationsModule = module as ModuleResourceVerification;

      return (
        fields[fieldKey]
          ?.filter((obj) => obj.type === verificationsModule['verification-type'])
          .map((obj) => obj as Verification)
          .filter(
            (object) =>
              verificationsModule['verification-templates'].length === 0 ||
              verificationsModule['verification-templates'].includes(
                // Some verifications do not have a verification template such as QA verifications

                // or verifications from Inquiry 2.0
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                object.relationships['verification-template'].data?.id,
              ),
          )
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .sort((a, b) => (a.attributes['completed-at'] > b.attributes['completed-at'] ? 1 : -1))
      );
    }
    case 'inquiries': {
      const inquiryTemplateId = (module as ModuleResourceInquiry)['inquiry-template'];

      return inquiryTemplateId
        ? fields[fieldKey]?.filter(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (obj: Inquiry) =>
              obj.relationships['inquiry-template'].data?.id === inquiryTemplateId ||
              obj.relationships['template'].data?.id === inquiryTemplateId,
          )
        : fields[fieldKey];
    }
    case 'accounts': {
      const accountTypeId = (module as ModuleResourceAccount)['account-type'];

      return accountTypeId
        ? fields[fieldKey]?.filter(
            (obj) => (obj as Account).relationships['account-type']?.data.id === accountTypeId,
          )
        : fields[fieldKey];
    }
    default:
      return fields[fieldKey];
  }
};

interface ResourceViewProps {
  module: ModuleResource;
  resource: CaseObject;
  documents: DocumentGeneric[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  inquiryVerifications: Verification[];
  transactionTypes: TransactionType[];
  transactionStatuses: TransactionStatus[];
  accountTypes: AccountType[];
  accountStatuses: AccountStatus[];
  reportTemplates: ReportTemplate[];
  featureFlags: FeatureFlagsMap;
  tags: Tag[];
  handleUpdateInquiryTags: (inquiryId: string, tags: string[]) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleUpdateReportTags: (reportId: string, tags: string[]) => void;
}

const ResourceView = ({
  module,
  resource,
  documents,
  inquiryTemplates,
  inquiryTemplateVersions,
  inquiryVerifications,
  transactionTypes,
  transactionStatuses,
  accountTypes,
  accountStatuses,
  reportTemplates,
  featureFlags,
  tags,
  handleUpdateInquiryTags,
  handleFlagInquiryIssues,
  handleFlagVerificationIssues,
  handleUpdateReportTags,
}: ResourceViewProps) => {
  switch (resource.type) {
    case ApiModel.Account: {
      const accountStatus = accountStatuses.find(
        (obj) => obj.id === resource.relationships['account-status']?.data.id,
      );
      const accountType = accountTypes.find(
        (obj) => obj.id === resource.relationships['account-type']?.data.id,
      );

      return (
        <ResourceAccountV2
          account={resource}
          accountStatus={accountStatus}
          accountType={accountType}
          titleFieldKey={(module as ModuleResourceAccount)['title-field-key']}
          hideEmptyAttributes={(module as ModuleResourceAccount)['hide-empty-attributes']}
          defaultAccordionOpen={(module as ModuleResourceAccount)['default-accordion-open']}
        />
      );
    }
    case ApiModel.Inquiry: {
      const objectDocuments = documents.filter(
        (doc) =>
          resource.relationships.documents.data.findIndex((idoc) => idoc.id === doc.id) !== -1,
      );
      const objectTags = tags.filter(
        (tag) => resource.relationships.tags.data.findIndex((itag) => itag.id === tag.id) !== -1,
      ) as Tag[];
      const objectVerifications = inquiryVerifications.filter(
        (ver) =>
          resource.relationships.verifications.data.findIndex((iver) => iver.id === ver.id) !== -1,
      );
      // TODO: (melissa) ideally inquiryTemplates would be included in CaseObjects so that
      // we can pass caseObjects to Resources without having to explicitly split them out
      // like this.
      return (
        <ResourceInquiry
          attributes={(module as ModuleResourceInquiry).attributes}
          hideEmptyAttributes={(module as ModuleResourceInquiry)['hide-empty-attributes']}
          inquiry={resource}
          documents={objectDocuments}
          tags={objectTags}
          inquiryTemplates={inquiryTemplates}
          inquiryTemplateVersions={inquiryTemplateVersions}
          verifications={objectVerifications}
          defaultAccordionOpen={module['default-accordion-open'] ?? true}
          featureFlags={featureFlags}
          handleUpdateInquiryTags={handleUpdateInquiryTags}
          handleFlagInquiryIssues={handleFlagInquiryIssues}
        />
      );
    }
    case ApiModel.VerificationAamva:
    case ApiModel.VerificationBusinessWebsite:
    case ApiModel.VerificationDatabase:
    case ApiModel.VerificationDatabaseAadhaar:
    case ApiModel.VerificationDatabaseStandard:
    case ApiModel.VerificationDatabaseBusiness:
    case ApiModel.VerificationDatabaseBusinessFootprint:
    case ApiModel.VerificationDatabaseEcbsv:
    case ApiModel.VerificationDatabasePhoneCarrier:
    case ApiModel.VerificationDatabaseTin:
    case ApiModel.VerificationDatabaseVatNumber:
    case ApiModel.VerificationDigitalIdUkSharecode:
    case ApiModel.VerificationDocument:
    case ApiModel.VerificationEmailAddress:
    case ApiModel.VerificationGovernmentId:
    case ApiModel.VerificationGovernmentIdNfc:
    case ApiModel.VerificationPhoneNumber:
    case ApiModel.VerificationSelfie: {
      return (
        <ResourceVerification
          verification={resource}
          documents={documents}
          defaultAccordionOpen={module['default-accordion-open'] ?? true}
          featureFlags={featureFlags}
          module={module as ResourceVerificationModules}
          handleFlagVerificationIssues={handleFlagVerificationIssues}
        />
      );
    }
    case ApiModel.ReportAddressLookup:
    case ApiModel.ReportAdverseMedia:
    case ApiModel.ReportChainalysisAddressScreening:
    case ApiModel.ReportCryptoAddressWatchlist:
    case ApiModel.ReportCustomList:
    case ApiModel.ReportEmailAddress:
    case ApiModel.ReportFinraBrokerCheck:
    case ApiModel.ReportMxAccount:
    case ApiModel.ReportPhoneNumber:
    case ApiModel.ReportPoliticallyExposedPerson:
    case ApiModel.ReportProfile:
    case ApiModel.ReportProfileNonAuthoritative:
    case ApiModel.ReportSecActionLookup:
    case ApiModel.ReportSentilinkApplicationRisk:
    case ApiModel.ReportSentilinkScores:
    case ApiModel.ReportSocialMedia:
    case ApiModel.ReportSynthetic:
    case ApiModel.ReportWatchlist:
    case ApiModel.ReportBusinessAdverseMedia:
    case ApiModel.ReportBusinessAssociatedPersons:
    case ApiModel.ReportBusinessClassification:
    case ApiModel.ReportBusinessIndustryClassification:
    case ApiModel.ReportBusinessLiensLookup:
    case ApiModel.ReportBusinessLookup:
    case ApiModel.ReportBusinessNonprofit:
    case ApiModel.ReportBusinessOnlinePresence:
    case ApiModel.ReportBusinessRegistrationsLookup:
    case ApiModel.ReportBusinessSocialMedia:
    case ApiModel.ReportBusinessWatchlist:
    case ApiModel.ReportBusinessWebsite: {
      const reportTemplate = reportTemplates.find(
        (obj) => obj.id === resource.relationships['report-template']?.data.id,
      );

      const objectTags = tags.filter(
        (tag) => resource.relationships.tags?.data.findIndex((itag) => itag.id === tag.id) !== -1,
      ) as Tag[];

      return (
        <ResourceReport
          report={resource as Report}
          reportTemplate={reportTemplate as ReportTemplate}
          defaultAccordionOpen={module['default-accordion-open'] ?? true}
          featureFlags={featureFlags}
          tags={objectTags}
          handleUpdateReportTags={handleUpdateReportTags}
        />
      );
    }

    case ApiModel.ReportRunCustomList:
      return (
        <ResourceReportRun
          reportRun={resource as ReportRun}
          defaultAccordionOpen={module['default-accordion-open'] ?? true}
          featureFlags={featureFlags}
        />
      );
    case ApiModel.Transaction: {
      const transactionStatus = transactionStatuses.find(
        (obj) => obj.id === resource.relationships['transaction-status']?.data?.id,
      );
      const transactionType = transactionTypes.find(
        (obj) => obj.id === resource.relationships['transaction-type']?.data.id,
      );
      return (
        <ResourceTransaction
          transaction={resource}
          transactionStatus={transactionStatus}
          transactionType={transactionType}
          hideEmptyAttributes={(module as ModuleResourceTransaction)['hide-empty-attributes']}
          defaultAccordionOpen={module['default-accordion-open'] ?? true}
        />
      );
    }
    default:
      return (
        <h1>
          {resource.type} {resource.id}
        </h1>
      );
  }
};

interface GenerateTableParamsProps {
  defaultVisibleKeys: string[];
  resources: CaseObject[];
  tags: Tag[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
}

export const generateTableParams = ({
  defaultVisibleKeys,
  resources,
  tags,
  transactionStatuses,
  transactionTypes,
}: GenerateTableParamsProps) => {
  // TODO: (melissa) FOR NOW we assume that all resources will be of the same type
  const resourceType = resources[0].type;
  switch (resourceType) {
    case ApiModel.Transaction: {
      const columnIdToLabel = generateColumnIdToLabelTxns({ transactionTypes: transactionTypes });
      return {
        cardTitle: `Transactions (${resources.length})`,
        nullStateText: 'No transactions found',
        defaultVisibleKeys: defaultVisibleKeys,
        columnIdToLabel: columnIdToLabel,
        columnDefs: generateColumnDefsTxns({
          columnIdToLabel: columnIdToLabel,
          tags: tags,
          transactionStatuses: transactionStatuses,
          transactionTypes: transactionTypes,
        }),
        tableData: generateTableDataTxns({ transactions: resources as Transaction[] }),
      };
    }
  }
};

export default Resource;
